import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Collapse,
  Flex,
  Input,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
} from '@chakra-ui/react';
import { CaretDown, EnvelopeSimple, Info, Warning } from 'phosphor-react';

/**
 * Relatório de assinaturas ao supervisor (15/10): enquanto o contrato está em assinatura, o back manda
 * aos e-mails daqui quem já assinou e quem falta, nos dias e na hora escolhidos (ou uma vez, numa data);
 * quando todos assinam, uma confirmação. Sem e-mail, nada sai.
 */
export interface IRelatorioAssinaturas {
  emails: string[];
  /** Dias da semana, ISO: 1 = segunda ... 7 = domingo. */
  dias: number[];
  hora: string;
  /** Preenchida = uma vez só, nesse dia (`YYYY-MM-DD`); vazia = nos dias da semana. */
  data: string | null;
  /** Sobrou texto recusado no campo de e-mail: quem usa trava o salvar (senão o e-mail some calado). */
  invalido?: boolean;
}

export const RELATORIO_PADRAO: IRelatorioAssinaturas = {
  emails: [],
  dias: [2, 4],
  hora: '08:00',
  data: null,
};

const DIAS = [
  { valor: 1, rotulo: 'Seg', nome: 'Segunda' },
  { valor: 2, rotulo: 'Ter', nome: 'Terça' },
  { valor: 3, rotulo: 'Qua', nome: 'Quarta' },
  { valor: 4, rotulo: 'Qui', nome: 'Quinta' },
  { valor: 5, rotulo: 'Sex', nome: 'Sexta' },
  { valor: 6, rotulo: 'Sáb', nome: 'Sábado' },
  { valor: 7, rotulo: 'Dom', nome: 'Domingo' },
];

// O back limita a 20 e confere com EmailStr (email-validator): a regra daqui é a dele ou mais estrita (sem
// acento na parte local), para o 400 não derrubar o salvar.
const MAX_EMAILS = 20;
const EMAIL =
  /^[a-z0-9!#$%&'*+/=?^_`{|}~.-]{1,64}@([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i;
const DOMINIO_RESERVADO = /\.(local|localhost|test|invalid|onion|arpa)$/i;

export function emailValido(email: string): boolean {
  const local = email.split('@')[0] ?? '';
  return (
    EMAIL.test(email) &&
    !DOMINIO_RESERVADO.test(email) &&
    !local.startsWith('.') &&
    !local.endsWith('.') &&
    !local.includes('..')
  );
}

const diaLocal = (deslocamento = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + deslocamento);
  return d.toLocaleDateString('sv-SE'); // YYYY-MM-DD no fuso do navegador
};

/** O que o back devolve do contrato (`relatorio_*`) para o formulário. */
export function relatorioDoContrato(c?: {
  relatorio_emails?: string[];
  relatorio_dias?: number[];
  relatorio_hora?: string;
  relatorio_data?: string | null;
}): IRelatorioAssinaturas {
  return {
    emails: c?.relatorio_emails ?? RELATORIO_PADRAO.emails,
    dias: c?.relatorio_dias ?? RELATORIO_PADRAO.dias,
    hora: c?.relatorio_hora ?? RELATORIO_PADRAO.hora,
    data: c?.relatorio_data ?? null,
  };
}

/** Campos do contrato no back (criar e editar). */
export function relatorioParaApi(r: IRelatorioAssinaturas) {
  return {
    relatorio_emails: r.emails,
    relatorio_dias: r.dias,
    relatorio_hora: r.hora,
    relatorio_data: r.data,
  };
}

/** Os mesmos campos na criação pelo card do quadro (`contrato_*`). */
export function relatorioParaCard(r: IRelatorioAssinaturas) {
  return {
    contrato_relatorio_emails: r.emails,
    contrato_relatorio_dias: r.dias,
    contrato_relatorio_hora: r.hora,
    contrato_relatorio_data: r.data,
  };
}

const juntar = (itens: string[]) =>
  itens.length > 1
    ? `${itens.slice(0, -1).join(', ')} e ${itens[itens.length - 1]}`
    : itens.join('');

/** Uma linha para a seção fechada: "Ter e Qui às 08:00 · 1 e-mail". */
export function resumoDoRelatorio(r: IRelatorioAssinaturas): string {
  if (r.emails.length === 0) return 'Desligado (sem e-mail)';
  const emails = `${r.emails.length} e-mail${r.emails.length > 1 ? 's' : ''}`;
  if (!r.data && r.dias.length === 0) return `Nenhum dia marcado · ${emails}`;
  let quando = juntar(DIAS.filter(d => r.dias.includes(d.valor)).map(d => d.rotulo));
  if (r.data) quando = `Em ${r.data.slice(8, 10)}/${r.data.slice(5, 7)}`;
  else if (r.dias.length === 7) quando = 'Todo dia';
  const passou = r.data && new Date(`${r.data}T${r.hora}`).getTime() + 3600000 <= Date.now();
  return `${quando} às ${r.hora}${passou ? ' (já passou)' : ''} · ${emails}`;
}

/**
 * Junta o texto digitado à lista (aceita lista colada, também "Nome <email>" do Outlook/Gmail). Devolve o valor
 * novo, o que sobrou (recusado) e o motivo. Pura: o "Salvar relatório" usa direto, sem esperar o estado.
 */
function confirmarDigitado(value: IRelatorioAssinaturas, digitado: string) {
  const partes = digitado
    .replace(/"[^"]*"/g, ' ') // nome entre aspas pode ter vírgula ("Silva, Bruno")
    .replace(/[^<>,;]*<([^<>]*)>/g, ' $1 ')
    .split(/[\s,;]+/)
    .map(p => p.trim().toLowerCase())
    .filter(Boolean);
  const emails = [...value.emails];
  const sobra: string[] = [];
  let motivo = '';
  partes.forEach(p => {
    if (!emailValido(p)) {
      sobra.push(p);
      motivo = 'E-mail inválido.';
    } else if (!emails.includes(p)) {
      if (emails.length < MAX_EMAILS) emails.push(p);
      else {
        sobra.push(p);
        motivo = `No máximo ${MAX_EMAILS} e-mails.`;
      }
    }
  });
  return {
    valor: { ...value, emails, invalido: sobra.length > 0 },
    sobra: sobra.join(', '),
    motivo,
  };
}

/** Botão de salvar do lugar que usa (ex.: "Salvar relatório" no card), no fim do editor. */
export interface ISalvarRelatorio {
  rotulo: string;
  onSalvar: (valor: IRelatorioAssinaturas) => void;
  isLoading?: boolean;
  /** Sem mudança para salvar (o texto digitado e ainda não confirmado conta como mudança). */
  isDisabled?: boolean;
}

interface Props {
  value: IRelatorioAssinaturas;
  onChange: (valor: IRelatorioAssinaturas) => void;
  salvar?: ISalvarRelatorio;
}

export default function RelatorioAssinaturas({ value, onChange, salvar }: Props) {
  const [digitado, setDigitado] = useState('');
  const [erro, setErro] = useState('');
  const campoRef = useRef<HTMLInputElement>(null);
  const umaVez = value.data !== null;
  // O back manda até 1 h depois do horário marcado; passou disso, a vez se perdeu.
  const jaPassou =
    umaVez && new Date(`${value.data}T${value.hora}`).getTime() + 3600000 <= Date.now();

  // Enter, vírgula, ponto e vírgula ou sair do campo: o e-mail digitado não se perde ao clicar em salvar.
  // Aceita uma lista colada (também "Nome <email>" do Outlook/Gmail); o que não vale fica no campo, com o
  // aviso, e o salvar fica travado até corrigir ou apagar.
  const adicionar = () => {
    if (!digitado.trim()) return;
    const { valor, sobra, motivo } = confirmarDigitado(value, digitado);
    if (valor.emails.length !== value.emails.length || valor.invalido !== !!value.invalido)
      onChange(valor);
    setDigitado(sobra);
    setErro(motivo);
  };

  // O clique no salvar não tira o foco do campo (`onMouseDown`): com o blur antes, o e-mail virava etiqueta, o
  // campo crescia e o botão descia entre o apertar e o soltar — o clique se perdia. O digitado entra aqui.
  const aoSalvar = () => {
    if (!salvar) return;
    if (!digitado.trim()) {
      salvar.onSalvar(value);
      return;
    }
    const { valor, sobra, motivo } = confirmarDigitado(value, digitado);
    onChange(valor);
    setDigitado(sobra);
    setErro(motivo);
    if (!sobra) salvar.onSalvar(valor);
  };

  const alternarDia = (dia: number) =>
    onChange({
      ...value,
      dias: value.dias.includes(dia)
        ? value.dias.filter(d => d !== dia)
        : [...value.dias, dia].sort((a, b) => a - b),
    });

  const opcao = (ativa: boolean) => ({
    variant: ativa ? 'solid' : 'outline',
    // O tema ignora colorScheme e o hover do `solid` é branco: sem isto o escolhido parece desfeito.
    _hover: ativa ? { bg: 'primary', color: 'white' } : undefined,
    'aria-pressed': ativa,
  });

  // Aviso de baixo: o que impede o envio vem antes da explicação.
  let aviso = '';
  if (!umaVez && value.dias.length === 0) aviso = 'Nenhum dia marcado: não envia.';
  else if (jaPassou) aviso = 'Esse dia e horário já passaram: não envia.';

  return (
    <Flex direction="column" gap={3}>
      <Box>
        <Rotulo>Destinatários</Rotulo>
        {/* E-mails como etiquetas dentro do próprio campo: sem uma linha por etiqueta. */}
        <Flex
          wrap="wrap"
          align="center"
          gap={1.5}
          px={1}
          py={1.5}
          minH="36px"
          bg="white"
          border="1px solid"
          borderColor={erro ? 'red.400' : 'gray.200'}
          borderRadius="md"
          cursor="text"
          _focusWithin={{ borderColor: erro ? 'red.400' : 'blue.400' }}
          onClick={() => campoRef.current?.focus()}
        >
          {value.emails.map(email => (
            <Tag
              key={email}
              size="sm"
              borderRadius="full"
              variant="subtle"
              colorScheme="blue"
              maxW="100%"
            >
              <TagLabel fontSize="11px" title={email}>
                {email}
              </TagLabel>
              <TagCloseButton
                aria-label={`Remover ${email}`}
                onClick={() =>
                  onChange({ ...value, emails: value.emails.filter(e => e !== email) })
                }
              />
            </Tag>
          ))}
          <Input
            ref={campoRef}
            variant="unstyled"
            size="sm"
            h="24px"
            flex="1"
            minW="120px"
            fontSize="13px"
            type="email"
            aria-label="E-mail do supervisor"
            placeholder={value.emails.length ? 'Adicionar outro e-mail' : 'E-mail do supervisor'}
            value={digitado}
            onChange={e => {
              setDigitado(e.target.value);
              setErro('');
              if (value.invalido) onChange({ ...value, invalido: false });
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
                e.preventDefault();
                adicionar();
              }
            }}
            onBlur={adicionar}
          />
        </Flex>
        {erro && (
          <Text fontSize="11px" color="red.500" mt={1}>
            {erro}
          </Text>
        )}
      </Box>

      <Box>
        <Rotulo>Quando</Rotulo>
        <ButtonGroup size="xs" isAttached mb={2}>
          <Button
            fontSize="11px"
            px={3}
            {...opcao(!umaVez)}
            onClick={() => onChange({ ...value, data: null })}
          >
            Semanal
          </Button>
          <Button
            fontSize="11px"
            px={3}
            {...opcao(umaVez)}
            onClick={() => !umaVez && onChange({ ...value, data: diaLocal(1) })}
          >
            Uma vez
          </Button>
        </ButtonGroup>

        {umaVez ? (
          <Input
            type="date"
            size="sm"
            fontSize="13px"
            borderRadius="md"
            bg="white"
            aria-label="Data do envio"
            min={diaLocal()}
            value={value.data ?? ''}
            onChange={e => e.target.value && onChange({ ...value, data: e.target.value })}
          />
        ) : (
          // Os 7 dias em bolinhas com a inicial (o nome inteiro no title e no leitor de tela): com 3 letras, na
          // lateral do card, cada botão ficava com 21 px e o "Dom" estourava.
          <Flex justify="space-between">
            {DIAS.map(d => (
              <Button
                key={d.valor}
                size="xs"
                w="26px"
                h="26px"
                minW="26px"
                p={0}
                borderRadius="full"
                fontSize="11px"
                fontWeight="600"
                aria-label={d.nome}
                title={d.nome}
                {...opcao(value.dias.includes(d.valor))}
                onClick={() => alternarDia(d.valor)}
              >
                {d.rotulo[0]}
              </Button>
            ))}
          </Flex>
        )}

        {/* Horário na própria linha: espremido ao lado da repetição, cortava em fonte maior. */}
        <Flex align="center" justify="space-between" gap={2} mt={2}>
          <Text fontSize="12px" color="gray.600">
            Horário
          </Text>
          <Input
            type="time"
            size="sm"
            w="auto"
            minW="104px"
            fontSize="13px"
            borderRadius="md"
            bg="white"
            aria-label="Horário do envio"
            value={value.hora}
            onChange={e =>
              e.target.value && onChange({ ...value, hora: e.target.value.slice(0, 5) })
            }
          />
        </Flex>
      </Box>

      <Flex gap={1.5} align="flex-start" color={aviso ? 'orange.600' : 'gray.500'}>
        <Box flexShrink={0} mt="1px">
          {aviso ? <Warning size={14} /> : <Info size={14} />}
        </Box>
        <Text fontSize="11px" lineHeight="1.4">
          {aviso ||
            'Só enquanto o contrato está em assinatura (horário de Brasília). No fim, vai uma confirmação.'}
        </Text>
      </Flex>

      {salvar && (
        <Button
          size="xs"
          colorScheme="blue"
          w="100%"
          onMouseDown={e => e.preventDefault()}
          onClick={aoSalvar}
          isLoading={salvar.isLoading}
          isDisabled={(salvar.isDisabled && !digitado.trim()) || !!value.invalido}
        >
          {salvar.rotulo}
        </Button>
      )}
    </Flex>
  );
}

function Rotulo({ children }: { children: React.ReactNode }) {
  return (
    <Text fontSize="11px" fontWeight="600" color="gray.500" mb={1.5}>
      {children}
    </Text>
  );
}

interface SecaoProps extends Props {
  /** Há edição ainda não salva (o resumo avisa: a seção fechada não mostra o botão). */
  alterado?: boolean;
}

/**
 * A seção recolhível: fechada, só o resumo; aberta, o editor. Com e-mail recusado no campo ela não fecha (o
 * salvar fica travado por ele e precisa estar à vista).
 */
export function SecaoRelatorio({ value, onChange, salvar, alterado = false }: SecaoProps) {
  const [aberta, setAberta] = useState(false);
  // E-mail recusado abre e prende a seção: o clique no cabeçalho não vira um "fechar" escondido que a fecharia
  // sozinha quando o e-mail fosse corrigido.
  useEffect(() => {
    if (value.invalido) setAberta(true);
  }, [value.invalido]);
  const mostrar = aberta || !!value.invalido;
  const resumo = value.invalido ? 'E-mail inválido no campo' : resumoDoRelatorio(value);
  return (
    <Box>
      <Flex
        as="button"
        type="button"
        w="100%"
        align="center"
        gap={2}
        textAlign="left"
        aria-expanded={mostrar}
        aria-label={`Relatório de assinaturas: ${resumo}${alterado ? ' (não salvo)' : ''}`}
        onClick={() => !value.invalido && setAberta(a => !a)}
      >
        <Box flex="1" minW={0}>
          {/* Mesmo estilo dos cabeçalhos vizinhos da lateral (CONTRATO, EXPIRACAO). */}
          <Flex align="center" gap={1}>
            <EnvelopeSimple size={12} color="#A0AEC0" weight="bold" />
            {/* Abreviado e numa linha: "Relatório de assinaturas" quebrava em duas na lateral do card. */}
            <Text
              fontSize="11px"
              fontWeight="600"
              color="gray.400"
              textTransform="uppercase"
              letterSpacing="0.05em"
              noOfLines={1}
              title="Relatório de assinaturas"
            >
              Relatório de ass.
            </Text>
          </Flex>
          <Text
            fontSize="12px"
            mt={0.5}
            color={value.invalido ? 'red.500' : 'gray.600'}
            noOfLines={1}
          >
            {resumo}
            {alterado && !value.invalido && (
              <Text as="span" color="orange.600">
                {' '}
                · não salvo
              </Text>
            )}
          </Text>
        </Box>
        <CaretDown
          size={14}
          color="#A0AEC0"
          style={{ transform: mostrar ? 'rotate(180deg)' : undefined, transition: 'transform .2s' }}
        />
      </Flex>
      <Collapse in={mostrar} animateOpacity>
        <Box mt={3} p={3} bg="white" border="1px solid" borderColor="gray.100" borderRadius="lg">
          <RelatorioAssinaturas value={value} onChange={onChange} salvar={salvar} />
        </Box>
      </Collapse>
    </Box>
  );
}
